import { useState } from "react";
import classNames from "classnames";
import { Field } from "formik";
import { crossIcon } from "../../../../assets/icons";
import {
  COLORS,
  COLOR_TYPES,
  DEFAULT_LOGO_URL,
  DEFAULT_BRAND_LOGO_URL,
  EMPTY_STRING,
} from "../../../../constants";
import { convertCamelCaseToCapitalize } from "../../../../helpers";
import { PopOver } from "../../../atoms";

const { CONTRAST, PRIMARY_COLOR, SECONDARY_COLOR } = COLORS;

const CustomizeApp = ({
  values,
  handleBlur,
  handleChange,
  touched,
  errors,
  setFieldValue,
  handleImageUpload,
  appLogoBase64,
  handleResetImage,
  brandLogoBase64,
}) => {
  const [shouldShowColorPicker, setShouldShowColorPicker] = useState(false);

  const getTooltipMessage = (type) => {
    switch (type) {
      case PRIMARY_COLOR:
        return "Brand Identity: The primary color defines your site's brand and is featured in icons and design.";
      case SECONDARY_COLOR:
        return "Harmonious UI: The secondary color, a shade of the primary, creates a smooth UI.";
      case CONTRAST:
        return "Readability Focus: Contrast color ensures text and layout clarity for a clear visual hierarchy.";
      default:
        return EMPTY_STRING;
    }
  };

  return (
    <div className="scrollbar flex-1 overflow-auto">
      <div className="space-y-4 p-6 border-b border-gray-700">
        <div className="form-group">
          <label className="text-sm font-medium text-gray-50 mb-1 block">
            Brand
          </label>
          <Field
            name="brandName"
            type="text"
            className={classNames("form-control border-2", {
              "border-red-600": touched.brandName && errors.brandName,
            })}
            placeholder="brandName"
            value={values.brandName}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          {touched.brandName && errors.brandName && (
            <div className="font-medium text-red-500 text-sm">
              {errors.brandName}
            </div>
          )}
        </div>
        <div className="form-group">
          <div className="flex justify-between mb-1">
            <div className="text-sm font-medium text-gray-50">
              Select Brand Logo
            </div>
            {values.logoWithBrand !== DEFAULT_BRAND_LOGO_URL && (
              <button
                type="button"
                className="rounded-md hover:bg-gray-900"
                onClick={() => handleResetImage(setFieldValue, "logoWithBrand")}
              >
                <img src={crossIcon} alt="cross" className="w-4" />
              </button>
            )}
          </div>
          <label>
            <div className="border-2 border-gray-300 border-dashed rounded-md p-7 grid place-content-center cursor-pointer">
              <span>
                <div className="text-center">
                  <Field name="logoWithBrand">
                    {() => (
                      <div>
                        <img
                          src={brandLogoBase64}
                          alt="Default Logo"
                          className="flex w-28 h-100"
                        />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(event) =>
                            handleImageUpload(
                              event,
                              setFieldValue,
                              "logoWithBrand"
                            )
                          }
                          onClick={(event) => (event.target.value = null)}
                        />
                      </div>
                    )}
                  </Field>
                </div>
              </span>
            </div>
          </label>
        </div>
        <div className="form-group">
          <div className="flex justify-between mb-1">
            <div className="text-sm font-medium text-gray-50">Select Logo</div>
            {values.logo !== DEFAULT_LOGO_URL && (
              <button
                type="button"
                className="rounded-md hover:bg-gray-900"
                onClick={() => handleResetImage(setFieldValue, "logo")}
              >
                <img src={crossIcon} alt="cross" className="w-4" />
              </button>
            )}
          </div>
          <label>
            <div className="border-2 border-gray-300 border-dashed rounded-md p-7 grid place-content-center cursor-pointer">
              <span>
                <div className="text-center">
                  <Field name="logo">
                    {() => (
                      <div>
                        <img
                          src={appLogoBase64}
                          alt="Default Logo"
                          className="flex w-28 h-100"
                        />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(event) =>
                            handleImageUpload(event, setFieldValue, "logo")
                          }
                          onClick={(event) => (event.target.value = null)}
                        />
                      </div>
                    )}
                  </Field>
                </div>
              </span>
            </div>
          </label>
        </div>
      </div>
      <div className="space-y-4 p-6">
        <div className="form-group">
          <label className="text-lg font-medium text-gray-50 mb-1 block">
            Choose Color Scheme
          </label>
          {COLOR_TYPES.map((colorType) => (
            <div key={colorType}>
              <div className="flex flex-row items-center mb-1 mt-2">
                <label className="text-sm font-medium text-gray-50 block">
                  {convertCamelCaseToCapitalize(colorType)}
                </label>
                <div
                  className="hover:cursor-pointer"
                  onClick={(e) => e.preventDefault()}
                >
                  <PopOver
                    options={getTooltipMessage(colorType)}
                    classes="bottom-1 left-7"
                    icon="info"
                  />
                </div>
              </div>
              <div
                className="form-control relative bg-white flex items-center px-3 cursor-pointer"
                onClick={() => setShouldShowColorPicker(!shouldShowColorPicker)}
              >
                <input
                  className={classNames(
                    "absolute top-0 left-0 h-full w-full opacity-0 cursor-pointer",
                    { hidden: shouldShowColorPicker }
                  )}
                  type="color"
                  value={values[colorType]}
                  onChange={(e) => setFieldValue([colorType], e.target.value)}
                />
                <div
                  className="w-12 h-12 -ml-3 rounded-md"
                  style={{
                    backgroundColor: `${values[colorType]}`,
                  }}
                ></div>
                <span className="text-gray-900 mr-4 ml-4 text-lg">
                  {values[colorType]}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CustomizeApp;
